import React, { useState, useEffect } from "react";
import Graph from "react-graph-vis";
import styled from "styled-components";
import { Container, Row, Col } from "react-bootstrap";
import Particle from "../Particle";
import VerticalNavBar from "../VerticalNavBar";
import { HiOutlineCubeTransparent } from "react-icons/hi";
import { PiGraph } from "react-icons/pi";
import API from "../../utils/api";
import {
  Card as MuiCard,
  CardContent,
  Typography,
  Button as MuiButton,
  Box,
} from "@mui/material";

import { format } from "date-fns";
import { marked } from "marked";
import DOMPurify from "dompurify";

const Card = styled(MuiCard)`
  background-color: #1b1a2ea9;
  color: white;
  box-shadow: 0 4px 5px 3px rgba(119, 53, 136, 0.459);
  transition: all 0.5s ease 0.5s;
  &:hover {
    ${"" /* transform: scale(1.02); */}
    box-shadow: 0 4px 4px 5px rgba(129, 72, 144, 0.561);
  }
`;

const DateTypography = styled(Typography)`
  font-size: 1rem;
  text-align: right;
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
  color: var(--imp-text-color);
`;

const TitleTypography = styled(Typography)`
  font-size: 1.5rem;
  @media (max-width: 768px) {
    font-size: 1.025rem;
  }
  font-weight: 500;
  color: white;
`;

const LinkToResourceButton = styled(MuiButton)`
  background-color: #623686;
  color: #fff;
  &:hover {
    background-color: #6d20c5d7;
  }
`;

const ContentBox = styled(Box)`
  color: white;
  white-space: normal;
  text-align: left;
  font-family: "Montserrat", sans-serif;
  font-optical-sizing: auto;
  font-style: normal;
  letter-spacing: 0.5px;
  word-spacing: 1px;
  font-size: 1.2rem;
  @media (max-width: 768px) {
    font-size: 0.725rem;
  }
  /* Remove margin and padding from paragraphs or line breaks */
  p {
    margin: 0;
    padding: 0.35rem;
    @media (max-width: 768px) {
      padding: 0rem;
    }
  }
  h3 {
    @media (max-width: 768px) {
      font-size: 0.9rem;
    }
  }
  h4 {
    @media (max-width: 768px) {
      font-size: 0.8rem;
    }
  }
  h5,
  h6 {
    @media (max-width: 768px) {
      font-size: 0.7rem;
    }
  }
  br {
    margin: 0;
    padding: 0.5rem;
    @media (max-width: 768px) {
      padding: 0rem;
    }
  }
`;

const StyledContainer = styled(Container)`
  height: 20rem;
  border: 0.25rem solid #fff;
  border-radius: 5px;

  // Media query for mobile
  @media (max-width: 768px) {
    height: 20rem; // Adjust height for mobile
  }
`;
const formatDate = (date) => {
  return format(new Date(date), "EEE, do MMM yyyy: h:mma");
};
function LearningGraph(props = {}) {
  const [graph, setGraph] = useState({ nodes: [], edges: [] }); // Initialize empty graph
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({});
  props.setLearning(true);
  // Convert Markdown to HTML
  const createMarkup = (markdownText) => {
    const html = marked(markdownText);
    return { __html: DOMPurify.sanitize(html) };
  };
  useEffect(() => {
    // Fetch nodes and edges when the component mounts
    API.get("/api/learning/getGraph")
      .then((res) => res.data)
      .then((res) => res.data)
      .then((data) => {
        // Transform the data into the format required by react-graph-vis
        const formattedGraph = {
          nodes: data.nodes.map((node) => {
            // Determine the node style based on its type
            const isParent = node.type === "parent";
            const isRevision = node.selectedForRevision;

            return {
              dataId: node.data,
              name: node.label,
              type: node.type,
              id: node._id,
              label: !isParent ? undefined : node.label, // Hide labels for parent nodes
              borderWidth: 2,
              color: {
                border: isParent
                  ? "#8B0000"
                  : isRevision
                  ? "#007BFF"
                  : "#9d41e7", // Blue for "revision"
                background: isParent
                  ? "#4B0000"
                  : isRevision
                  ? "#0056b3"
                  : "#000",
                highlight: {
                  border: isParent
                    ? "#FF6347"
                    : isRevision
                    ? "#89CFF0"
                    : "#9d41e7",
                  background: isParent
                    ? "#FFB6C1"
                    : isRevision
                    ? "#ADD8E6"
                    : "#D2E5FF",
                },
                hover: {
                  border: isParent
                    ? "#FF4500"
                    : isRevision
                    ? "#4682B4"
                    : "#9d41e7",
                  background: isParent
                    ? "#FA8072"
                    : isRevision
                    ? "#B0C4DE"
                    : "#D2E5FF",
                },
              },
              shadow: { enabled: true },
              font: {
                size: isParent ? 10 : 7, // Larger font size for "parent" nodes
                color: "#fff",
              },
            };
          }),
          edges: data.edges.map((edge) => {
            const isSpecial = edge.type === "special";
            return {
              length: isSpecial ? 300 : edge.type === "long" ? 150 : 40,
              from: edge.from._id,
              to: edge.to._id,
              label: edge.label,
              font: { size: 7 },
              color: isSpecial
                ? "#FFD700"
                : edge.type === "long"
                ? "#808080"
                : "#b873f1", // Gold for special edges, grey for long
            };
          }),
        };
        setGraph(formattedGraph);
      })
      .catch((error) => {
        console.error("Error loading the graph:", error);
      })
      .finally(() => setLoading(false));
  }, []);

  const options = {
    nodes: {
      shape: "dot",
      size: 5,
    },
    edges: {
      arrows: {
        to: {
          enabled: false,
        },
      },
      color: "#b873f1",
      length: 40,
    },
    physics: {
      enabled: true, // Enable physics
    },
    layout: { hierarchical: false, improvedLayout: false },
    interaction: {
      hover: true, // Enable hover interaction
    },
  };

  // Handle node click
  const handleNodeClick = (event) => {
    const clickedNodeId = event.nodes[0];
    const selectedNode = graph.nodes.find((node) => node.id === clickedNodeId);

    if (selectedNode.type === "parent") {
      // BFS to find and show child nodes
      const queue = [selectedNode.id];
      const visited = new Set();
      const visibleNodeIds = new Set();

      while (queue.length > 0) {
        const current = queue.shift();
        if (visited.has(current)) continue;
        visited.add(current);
        visibleNodeIds.add(current);

        // Find connected nodes
        graph.edges.forEach((edge) => {
          if (edge.from === current && !visited.has(edge.to)) {
            queue.push(edge.to);
          }
        });
      }

      // Update node visibility
      setGraph((prevGraph) => ({
        ...prevGraph,
        nodes: prevGraph.nodes.map((node) => ({
          ...node,
          label:
            visibleNodeIds.has(node.id) || node.type === "parent"
              ? node.name
              : undefined,
        })),
      }));

      setData({ parent: selectedNode.label });
    } else {
      // Make the clicked node label visible
      setGraph((prevGraph) => ({
        ...prevGraph,
        nodes: prevGraph.nodes.map((node) => ({
          ...node,
          label: node.id === selectedNode.id ? node.name : node.label,
        })),
      }));
      // Fetch data for the clicked non-parent node
      API.post("/api/learning/getNodeData", { id: selectedNode.dataId })
        .then((res) => res.data)
        .then((data) => {
          setData(data.learning);
        })
        .catch((err) => {
          alert("Error: ", err.error);
        });
    }
  };
  const events = {
    selectNode: handleNodeClick,
  };

  if (loading) {
    return <div>Loading graph...</div>; // Show loading indicator
  }

  return (
    <>
      <Container>
        <Particle />
      </Container>
      <Container fluid className="graph-section">
        <Col>
          <Row>
            <VerticalNavBar
              setIsAuthenticated={props.setIsAuthenticated}
              setLearning={props.setLearning}
            />
            <h1 style={{ color: "#fff" }}>
              <PiGraph /> Knowledge Graph
            </h1>
          </Row>
        </Col>
      </Container>
      <StyledContainer>
        <Graph graph={graph} options={options} events={events} />

        <br />
        {data.title ? (
          <Card>
            <CardContent>
              <DateTypography>{formatDate(data.lastRevised)}</DateTypography>
              <TitleTypography>{data.title}</TitleTypography>
              <br />
              <Box display="flex" justifyContent="flex-end"></Box>
              <Box display="flex" justifyContent="center">
                <ContentBox
                  dangerouslySetInnerHTML={createMarkup(data.description)}
                />
              </Box>
              <br />
              <Box display="flex" justifyContent="flex-end">
                {data.link !== "" && (
                  <LinkToResourceButton
                    size="small"
                    href={data.link}
                    target="_blank"
                  >
                    Resource Link
                  </LinkToResourceButton>
                )}
              </Box>
            </CardContent>
          </Card>
        ) : (
          <h3 style={{ color: "#fff", textAlign: "right" }}>
            <HiOutlineCubeTransparent /> {data.parent}
          </h3>
        )}
        <br />
        <br />
      </StyledContainer>
    </>
  );
}

export default LearningGraph;
